import { useCallback, useRef } from "react";

/**
 * Sichert einen Ladepfad gegen überholte Antworten ab.
 *
 * Ein Loader wird selten nur von einer Stelle aufgerufen: Effekt beim Betreten, Live-Ereignis,
 * Aktualisieren-Aktion und Reconnect-Abgleich schreiben denselben Zustand. Startet dabei eine
 * zweite Anfrage, bevor die erste zurück ist, darf die langsamere nicht gewinnen — sonst zeigt die
 * Fläche einen älteren Stand als den zuletzt angeforderten.
 *
 * Ein lokales Abbruch-Merkzeichen im Effekt reicht dafür nicht: es kennt nur den Effektpfad und
 * nicht die übrigen Aufrufer. Der Zähler hier liegt in einer Ref und gilt deshalb über **alle**
 * Aufrufer hinweg.
 *
 * ```ts
 * const startRun = useLatestRun();
 *
 * const reload = useCallback(async () => {
 *   const isCurrent = startRun();
 *   setLoading(true);
 *   try {
 *     const next = await api.getThing(id);
 *     if (isCurrent()) setThing(next);
 *   } finally {
 *     if (isCurrent()) setLoading(false);
 *   }
 * }, [id, startRun]);
 * ```
 *
 * `startRun` ist stabil und kann bedenkenlos in Abhängigkeitslisten stehen.
 *
 * Bewusst **kein** Abbruch der laufenden Anfrage: die Antwort wird nur verworfen. Ein echter
 * Abbruch bräuchte ein `AbortSignal` durch jede API-Funktion hindurch und würde am Ergebnis nichts
 * ändern — die überholte Antwort wird so oder so nicht angezeigt.
 */
export function useLatestRun() {
  const sequence = useRef(0);

  return useCallback(() => {
    const run = ++sequence.current;
    return () => run === sequence.current;
  }, []);
}

